import React, { useEffect, useRef, useState } from 'react'
import { Prompt, useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import MenuItem from '@mui/material/MenuItem'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import TextField from '@mui/material/TextField'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import DesktopDatePicker from '@mui/lab/DesktopDatePicker'
import koLocale from 'date-fns/locale/ko'
import Add from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup, { FORM_IMAGE_CLEAR_BUTTON_SX, FORM_INLINE_ACTION_BUTTON_SX, FORM_WIDE_UPLOAD_BUTTON_SX } from '../../component/BottomButtonGroup'
import AddressSearchModal from '../../component/AddressSearchModal'
import { FORM_IMAGE_PREVIEW_FRAME_SX, FORM_PANEL_SX, FORM_SUB_PANEL_SX } from '../../component/formSurfaceStyles'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import AnimalPackageSharingFields from '../animalPackages/AnimalPackageSharingFields'
import AnimalShippingPolicyFields from './AnimalShippingPolicyFields'
import {
    DEFAULT_NEW_ANIMAL_SHIPPING_POLICY,
    validateAnimalShippingPolicy,
} from './animalShippingPolicy'
import {
    ANIMAL_IMAGE_FILE_ACCEPT,
    ANIMAL_IMAGE_FILE_ERROR_MESSAGE,
    applyAnimalDraft,
    clearAnimalDraft,
    createAnimalDraftPayload,
    createDefaultSelectionOption,
    isAnimalImageFile,
    NEW_ANIMAL_DRAFT_KEY,
    NEW_ANIMAL_PACKAGE_DRAFT_KEY,
    normalizeAnimalSelectionOption,
    readAnimalDraft,
    saveAnimalDraft,
} from './animalDraftUtils'
import {
    ANIMAL_IMAGE_LEAVE_MESSAGE,
    hasPendingAnimalImages,
    usePendingAnimalImageUnloadGuard,
} from './animalLeaveGuard'
import {
    ANIMAL_PACKAGE_CODE,
    ANIMAL_PACKAGE_REQUIRED_AREA_M2,
    SEOUL25_REGIONS,
    buildAnimalPackageRegions,
    createDefaultAnimalPackageState,
    createSeoul25CategoryRows,
    isAnimalPackageDraftPayload,
    isValidAnimalPackageCode,
    isSeoul25CategoryRows,
    normalizeAnimalPackageCode,
    sortSeoul25CategoryRows,
    validateAnimalPackageRegions,
} from '../animalPackages/animalPackageUtils'

const NO_IMAGE = '/images/noimage.jpg'

const formSectionSx = {
    ...FORM_PANEL_SX,
    p: 2,
    mb: 1.5,
    boxShadow: 'none',
}

const imagePreviewCardSx = {
    ...FORM_IMAGE_PREVIEW_FRAME_SX,
    height: { xs: 220, md: 260 },
}

const imagePreviewSx = {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
}

const imageUploadPanelSx = {
    ...FORM_SUB_PANEL_SX,
    p: 1.25,
    minWidth: 0,
}

const imageCardHeaderSx = {
    minHeight: 24,
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    alignItems: 'center',
    columnGap: 1,
}

const courseRowSx = {
    mt: 1.5,
    p: 1.25,
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.default',
}

const optionStatusCardSx = (selected) => ({
    m: 0,
    p: 1.25,
    alignItems: 'flex-start',
    borderRadius: '4px',
    border: '1px solid',
    borderColor: selected ? 'primary.main' : 'rgba(148, 163, 184, 0.28)',
    bgcolor: selected ? 'rgba(25, 118, 210, 0.06)' : '#fff',
    '& .MuiRadio-root': {
        mt: -0.25,
    },
    '& .MuiFormControlLabel-label': {
        width: '100%',
    },
})

const OPTION_STATUS_OPTIONS = [
    {
        value: 0,
        title: '미사용',
        description: '옵션을 운영 화면에서 비활성 상태로 관리합니다.',
    },
    {
        value: 1,
        title: '사용',
        description: '옵션 입력값을 사용 상태로 저장합니다.',
    },
]

const VISIBILITY_OPTIONS = [
    { value: 1, label: '노출' },
    { value: 0, label: '미노출' },
]

// 긴 애니멀런 폼을 운영 목적별 카드로 나눠 등록/수정 화면의 흐름을 맞춘다.
function FormSection({ title, description, children, sx = {} }) {
    return (
        <Paper variant="outlined" sx={{ ...formSectionSx, ...sx }}>
            <Box sx={{ mb: 2, pb: 1.5, borderBottom: '1px solid rgba(148, 163, 184, 0.24)' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {title}
                </Typography>
                {description && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                        {description}
                    </Typography>
                )}
            </Box>
            {children}
        </Paper>
    )
}

function OptionStatusSetting({ label, value, onChange }) {
    return (
        <FormControl fullWidth>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {label}
            </Typography>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                    gap: 1,
                    mt: 1,
                }}
            >
                {OPTION_STATUS_OPTIONS.map((option) => {
                    const checked = Number(value ?? 0) === option.value

                    return (
                        <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={<Radio checked={checked} onChange={() => onChange(option.value)} />}
                            sx={optionStatusCardSx(checked)}
                            label={(
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {option.title}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {option.description}
                                    </Typography>
                                </Box>
                            )}
                        />
                    )
                })}
            </Box>
        </FormControl>
    )
}

// 이미지 미리보기와 숨김 file input을 한 카드로 묶어 세 이미지 행 레이아웃을 재사용한다.
function ImageUploadCard({
    title,
    image,
    imageAlt,
    inputId,
    inputName,
    uploadType,
    onUpload,
    canClear = false,
    hasPendingFile = false,
    onClear,
    disabled = false,
}) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, ...imageUploadPanelSx }}>
            <Box sx={imageCardHeaderSx}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                    {title}
                </Typography>
                {canClear && hasPendingFile && !disabled && (
                    <Button size="small" color="error" variant="text" startIcon={<RemoveIcon />} onClick={onClear} sx={FORM_IMAGE_CLEAR_BUTTON_SX}>
                        선택 취소
                    </Button>
                )}
            </Box>
            <Box sx={imagePreviewCardSx}>
                <Box component="img" src={image || NO_IMAGE} alt={imageAlt} sx={imagePreviewSx}/>
            </Box>
            <FormControl fullWidth>
                <TextField
                    inputProps={{ accept: ANIMAL_IMAGE_FILE_ACCEPT }}
                    style={{ display: "none" }}
                    name={inputName}
                    id={inputId}
                    type="file"
                    onChange={e => onUpload(e, uploadType)}
                    disabled={disabled}
                />
                <Box component="label" htmlFor={disabled ? undefined : inputId} sx={{ display: 'block' }}>
                    <Button fullWidth variant="contained" color="success" component="span" startIcon={<Add />} disabled={disabled} sx={FORM_WIDE_UPLOAD_BUTTON_SX}>
                        이미지 선택
                    </Button>
                </Box>
            </FormControl>
        </Box>
    )
}

export default function NewAnimal({ adminId, packageMode = false }) {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const listCurPage = locationState.curPage || 1
    const listSearchText = locationState.searchText || ''
    const listSearchSelect = locationState.searchSelect || { searchMeetType2: '', searchStatus: '' }
    const listSearch = location.search || ''
    const draftKey = packageMode ? NEW_ANIMAL_PACKAGE_DRAFT_KEY : NEW_ANIMAL_DRAFT_KEY
    const [data, setData] = useState({
        productId: null,
        adminId: adminId,
        meetType: 3,
        meetType2: 1,
        title: '',
        country: '',
        area: '',
        address: '전국 어디서든 비대면 마라톤',
        addressDetail: '',
        latitude: '',
        longitude: '',
        startTime: new Date(),
        endTime: new Date(),
        beginTime: new Date(),
        finishTime: new Date(),
        info1: '',
        info2: '010-5725-6820',
        info3: '',
        info4: '(주)러닝포인트',
        info5: '',
        info6: '',
        info7: '',
        paymentStatus: 1,
        couponStatus: 1,
        visibleStatus: 0,
        payQuantity: 1000,
        basicLike: 0,
        rStatus: packageMode ? 0 : 1,
        sStatus: packageMode ? 0 : 1,
        sOptionName: '우드케이스',
        sOptionPrice: 11000,
        sOptionQuantity: 999,
        isShow: 1,
        ...DEFAULT_NEW_ANIMAL_SHIPPING_POLICY,
        ...createDefaultAnimalPackageState(),
        isAnimalPackage: packageMode,
    })
    const [beginDay, setBeginDay] = useState(new Date())
    // const [beginHours, setBeginHours] = useState(0)
    // const [beginMinutes, setBeginMinutes] = useState(0)
    const [finishDay, setFinishDay] = useState(new Date())
    // const [finishHours, setFinishHours] = useState(0)
    // const [finishMinutes, setFinishMinutes] = useState(0)
    const [mainImage, setMainImage] = useState(NO_IMAGE)
    const [subImage1, setSubImage1] = useState(NO_IMAGE)
    const [recordImage, setRecordImage] = useState(NO_IMAGE)
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [openModal, setOpenModal] = useState(false)
    const [options, setOptions] = useState([{ name: '티셔츠(L)', quantity: 200, }, { name: '티셔츠(XL)', quantity: 200, }, { name: '티셔츠(XXL)', quantity: 200, },])
    const [selectionOptions, setSelectionOptions] = useState([createDefaultSelectionOption(0)])
    const [saving, setSaving] = useState(false)
    const [draftReady, setDraftReady] = useState(false)
    const [navigationUnlocked, setNavigationUnlocked] = useState(false)
    const toNumber = (value) => Number(value || 0)

    const inputArr = packageMode
        ? createSeoul25CategoryRows()
        : [
            { id: 0, type: 2, distance: 0, price: 30000, name: '', quantity: 200, status: 0, order: 0 },
            { id: 1, type: 2, distance: 0, price: 30000, name: '', quantity: 200, status: 0, order: 1 },
        ]

    const initialDraftStateRef = useRef(null)
    const initialDraftSnapshotRef = useRef('')
    const leaveGuardEnabled = hasPendingAnimalImages(data) && !saving && !navigationUnlocked

    usePendingAnimalImageUnloadGuard(leaveGuardEnabled)

    if (!initialDraftStateRef.current) {
        initialDraftStateRef.current = {
            data,
            arr: inputArr,
            options,
            selectionOptions,
            beginDay,
            finishDay,
        }
        initialDraftSnapshotRef.current = JSON.stringify(createAnimalDraftPayload({
            data,
            arr: inputArr,
            options,
            selectionOptions,
            beginDay,
            finishDay,
        }))
    }

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    // 저장/뒤로 이동 후에도 운영자가 보던 목록 조건으로 그대로 복귀시킨다.
    const moveToList = (snackbarState) => {
        setNavigationUnlocked(true)
        clearAnimalDraft(draftKey)

        // 저장 후 이동은 이탈 경고를 잠시 풀고, 목록 조건은 그대로 유지한다.
        setTimeout(() => {
            history.push(createListLinkTarget({
                pathname: packageMode ? '/animal-packages' : '/animals',
                search: listSearch,
                curPage: listCurPage,
                searchText: listSearchText,
                searchSelect: listSearchSelect,
                ...(snackbarState ? {
                    openSnackbar: true,
                    severitySnackbar: snackbarState.severity,
                    messageSnackbar: snackbarState.message,
                } : {})
            }))
        }, 0)
    }

    const handleBackClick = (event) => {
        if (leaveGuardEnabled) {
            event?.preventDefault()

            if (!window.confirm(ANIMAL_IMAGE_LEAVE_MESSAGE)) {
                return
            }
        }

        moveToList()
    }

    const [arr, setArr] = useState(inputArr)
    const totalLimitQuantity = arr.reduce((sum, item) => sum + toNumber(item.quantity), 0)

    useEffect(() => {
        let storedDraft = readAnimalDraft(draftKey)
        const initialDraftState = initialDraftStateRef.current

        if (packageMode && storedDraft && !isSeoul25CategoryRows(storedDraft.arr)) {
            clearAnimalDraft(draftKey)
            storedDraft = null
        } else if (!packageMode && isAnimalPackageDraftPayload(storedDraft)) {
            clearAnimalDraft(draftKey)
            storedDraft = null
        }

        if (storedDraft && initialDraftState) {
            const restoredDraft = applyAnimalDraft({
                baseData: initialDraftState.data,
                baseArr: initialDraftState.arr,
                baseOptions: initialDraftState.options,
                baseSelectionOptions: initialDraftState.selectionOptions,
                beginDay: initialDraftState.beginDay,
                finishDay: initialDraftState.finishDay,
                draft: storedDraft,
            })

            if (packageMode) {
                restoredDraft.data.isAnimalPackage = true
                restoredDraft.data.packageCode = restoredDraft.data.packageCode || ANIMAL_PACKAGE_CODE
                restoredDraft.data.requiredAreaM2 = restoredDraft.data.requiredAreaM2 || ANIMAL_PACKAGE_REQUIRED_AREA_M2
            }

            setData(restoredDraft.data)
            setArr(packageMode ? sortSeoul25CategoryRows(restoredDraft.arr) : restoredDraft.arr)
            setOptions(restoredDraft.options)
            setSelectionOptions(restoredDraft.selectionOptions)
            setBeginDay(restoredDraft.beginDay)
            setFinishDay(restoredDraft.finishDay)
        }

        setDraftReady(true)
    }, [draftKey, packageMode])

    useEffect(() => {
        if (!draftReady) {
            return
        }

        // 신규 애니멀런 등록은 큰 폼이라 목록 query 대신 sessionStorage로 draft를 이어간다.
        const nextDraft = createAnimalDraftPayload({
            data,
            arr,
            options,
            selectionOptions,
            beginDay,
            finishDay,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearAnimalDraft(draftKey)
            return
        }

        saveAnimalDraft(draftKey, nextDraft)
    }, [arr, beginDay, data, draftKey, draftReady, finishDay, options, selectionOptions])

    const addInput = () => {
        setArr(s => {
            return [
                ...s,
                {
                    id: arr[arr.length - 1].id + 1,
                    type: 2,
                    distance: 0,
                    price: 30000,
                    name: '',
                    quantity: 200,
                    status: 0,
                    order: arr[arr.length - 1].order + 1
                }
            ]
        })
    }

    const removeInput = (i) => {
        setArr(arr.filter((item, index) => index !== i))
    }

    const addSelectionOption = () => {
        setSelectionOptions((prevOptions) => [
            ...prevOptions,
            createDefaultSelectionOption(prevOptions.length),
        ])
    }

    const removeSelectionOption = (index) => {
        setSelectionOptions((prevOptions) => prevOptions.filter((_, itemIndex) => itemIndex !== index))
    }

    const updateSelectionOption = (index, name, value) => {
        setSelectionOptions((prevOptions) => prevOptions.map((item, itemIndex) => (
            itemIndex === index ? { ...item, [name]: value } : item
        )))
    }

    const getSubmittedCategoryRows = () => {
        const baseRows = arr.map((item, index) => ({
            ...item,
            status: 0,
            order: index,
            webVisible: Number(item.webVisible ?? 1),
            appVisible: Number(item.appVisible ?? 1),
        }))

        if (!Number(data.sStatus)) {
            return baseRows
        }

        const optionRows = selectionOptions.map((item, index) => {
            const option = normalizeAnimalSelectionOption(item, index)

            return {
                ...option,
                name: option.name.trim(),
                distance: 0,
                type: 2,
                status: 1,
                order: baseRows.length + index,
                webVisible: Number(option.webVisible ?? 1),
                appVisible: Number(option.appVisible ?? 0),
            }
        })

        return [...baseRows, ...optionRows]
    }

    const onChangeOptionType = (e, index) => {
        const value = e.target.value
        let newArr = [...arr]

        if (value === 0) {
            newArr[index].type = 0
            newArr[index].distance = 42
            setArr(newArr)
        } else if (value === 1) {
            newArr[index].type = 1
            newArr[index].distance = 21
            setArr(newArr)
        } else if (value === 2) {
            newArr[index].type = 2
            newArr[index].distance = 0
            setArr(newArr)
        }
    }

    const handleUpdateField = (e) => {
        if (e.target.name === 'rStatus' || e.target.name === 'sStatus') {
            setData({
                ...data,
                [e.target.name]: Number(e.target.value)
            })
        } else {
            setData({
                ...data,
                [e.target.name]: e.target.value
            })
        }
    }

    const handleOptionStatusChange = (name, value) => {
        if (name === 'sStatus' && Number(value) && selectionOptions.length === 0) {
            setSelectionOptions([createDefaultSelectionOption(0)])
        }

        setData((prevData) => ({
            ...prevData,
            [name]: Number(value),
        }))
    }

    const handleUploadClick = (e, type) => {
        e.preventDefault()

        const file = e.target.files?.[0]

        if (!file) {
            return
        }

        if (!isAnimalImageFile(file)) {
            e.target.value = ''
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(ANIMAL_IMAGE_FILE_ERROR_MESSAGE)
            return
        }

        let reader = new FileReader()

        reader.onloadend = () => {
            setData({
                ...data,
                [e.target.name]: file
            })
            if (type === 1) {
                setMainImage(reader.result)
            } else if (type === 2) {
                setSubImage1(reader.result)
            } else if (type === 3) {
                setRecordImage(reader.result)
            }
        }
    
        reader.readAsDataURL(file)
    }

    // 선택 이미지 취소는 저장 전 file input과 미리보기만 비운다. 서버 이미지 삭제 계약은 만들지 않는다.
    const handleClearSelectedImage = (fieldName, inputId) => {
        setData((prev) => {
            const nextData = { ...prev }
            delete nextData[fieldName]
            return nextData
        })

        const input = document.getElementById(inputId)
        if (input) {
            input.value = ''
        }

        if (fieldName === 'subImage1') {
            setSubImage1(NO_IMAGE)
        } else if (fieldName === 'recordImage') {
            setRecordImage(NO_IMAGE)
        }
    }

    const syncAnimalPackage = async (productId) => {
        const { data: detailResponse } = await API.post('/api/v1/admin/animal/getAnimal', { productId })

        if (detailResponse.code !== 1) {
            throw new Error(detailResponse.message || '패키지 상품 상세를 다시 불러오지 못했습니다.')
        }

        const regions = buildAnimalPackageRegions(detailResponse.categorys || [])

        if (!validateAnimalPackageRegions(regions)) {
            throw new Error('서울25 구 코스명을 확인해주세요.')
        }

        const { data: packageResponse } = await API.post('/api/v1/admin/animalPackage/upsert', {
            productId,
            packageCode: normalizeAnimalPackageCode(data.packageCode || ANIMAL_PACKAGE_CODE),
            progressPackageCode: normalizeAnimalPackageCode(data.progressPackageCode),
            requiredAreaM2: Number(data.requiredAreaM2 || ANIMAL_PACKAGE_REQUIRED_AREA_M2),
            status: 1,
            regions,
        })

        if (![1, 200].includes(Number(packageResponse.code))) {
            throw new Error(packageResponse.message || '패키지 구 매핑 저장에 실패했습니다.')
        }
    }

    const createData = async (params, { isPackageProduct = false } = {}) => {
        try {
            setSaving(true)

            const { data } = await API({
                method: 'post',
                url: '/api/v1/admin/animal/create',
                data: params,
                headers: { "Content-Type": "multipart/form-data" }
            })

            if (data.code === 1) {
                const productId = data.data?.productId

                if (isPackageProduct) {
                    if (!productId) {
                        throw new Error('상품 ID를 확인할 수 없어 패키지 구 매핑을 저장하지 못했습니다.')
                    }

                    try {
                        await syncAnimalPackage(productId)
                        moveToList({ severity: 'success', message: '서울25 패키지 상품과 구 매핑을 저장했습니다.' })
                    } catch (packageError) {
                        moveToList({
                            severity: 'warning',
                            message: `상품은 등록됐지만 패키지 구 매핑 저장에 실패했습니다. ${packageError.message}`,
                        })
                    }
                    return
                }

                moveToList({ severity: 'success', message: data.message || '애니멀런을 등록했습니다.' })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('애니멀런을 등록하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleComplete = (res) => {
        let fullAddress = res.address
        let extraAddress = ""

        if (res.addressType === "R") {
            if (res.bname !== "") {
                extraAddress += res.bname
            }
            if (res.buildingName !== "") {
                extraAddress += extraAddress !== "" ? `, ${res.buildingName}` : res.buildingName
            }
            fullAddress += extraAddress !== "" ? ` (${extraAddress})` : ""
        }

        data.country = res.sido
        data.area = res.sigungu
        if(res.sido.length > 2) {
            data.country = res.sido
        }
        
        data.address = fullAddress
        setOpenModal(false)

        fetch(`https://dapi.kakao.com/v2/local/search/address.json?query=${fullAddress}`, {
            method: 'GET',
            headers: { 
                "Content-Type": "application/json; charset=utf-8", 
                "Authorization": `KakaoAK ${process.env.REACT_APP_KAKAO_API_KEY}`
            }
        })
        .then(response => {
            return response.json()
        })
        .then(data => {
            // data.latitude = data.documents[0].y
            // data.longitude = data.documents[0].x
            data.latitude = data.latitude && data.latitude
            data.longitude = data.longitude && data.longitude
        })
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (saving) {
            return
        }

        const nextTitle = (data.title || '').trim()

        setData((prev) => ({
            ...prev,
            title: nextTitle
        }))

        if (!nextTitle) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('대회명을 입력해주세요.')
            return
        }

        if (arr.length === 0) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('코스를 등록해주세요.')
            return
        }

        let isOpt = true
        for (const item of options) {
            if (!item.name.trim()) {
                isOpt = false
            }
        }

        if (!isOpt) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('옵션명을 등록해주세요.')
            return
        }

        if (Number(data.sStatus)) {
            const hasInvalidSelectionOption = selectionOptions.length === 0 || selectionOptions.some((item) => !String(item.name || '').trim())

            if (hasInvalidSelectionOption) {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar('선택 옵션명을 등록해주세요.')
                return
            }
        }

        const shippingPolicyError = validateAnimalShippingPolicy(data)

        if (shippingPolicyError) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(shippingPolicyError)
            return
        }

        if (!data.mainImage) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('메인 이미지를 업로드해주세요.')
            return
        }

        const isPackageProduct = packageMode

        if (isPackageProduct) {
            const packageCode = normalizeAnimalPackageCode(data.packageCode)
            const progressPackageCode = normalizeAnimalPackageCode(data.progressPackageCode)

            if (!isValidAnimalPackageCode(packageCode)) {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar('패키지 코드는 영문 대문자, 숫자, 밑줄을 사용해 50자 이내로 입력해주세요.')
                return
            }

            if (packageCode === progressPackageCode) {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar('현재 패키지를 진행도 공유 기준으로 선택할 수 없습니다.')
                return
            }

            if (arr.filter((item) => Number(item.status || 0) === 0).length !== SEOUL25_REGIONS.length) {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar('서울25 패키지는 25개 구 코스가 필요합니다.')
                return
            }
        }

        let nextAddress = data.address
        let nextAddressDetail = data.addressDetail
        if (data.meetType === 2) {
            nextAddress = ''
        }
        if (data.meetType2 === 3) {
            nextAddress = ''
            nextAddressDetail = ''
        }

        const totalPayQuantity = arr.reduce((sum, item) => sum + toNumber(item.quantity), 0)
        const beginTime = new Date(beginDay)
        beginTime.setHours(0)
        beginTime.setMinutes(0)
        beginTime.setSeconds(0)
        const finishTime = new Date(finishDay)
        finishTime.setHours(23)
        finishTime.setMinutes(59)
        finishTime.setSeconds(59)
        const formData = new FormData()
        formData.append('adminId',data.adminId)
        if (data.mainImage instanceof File) formData.append('mainImage',data.mainImage)
        if (data.subImage1 instanceof File) formData.append('subImage1',data.subImage1)
        if (data.recordImage instanceof File) formData.append('recordImage',data.recordImage)
        formData.append('meetType',data.meetType)
        formData.append('meetType2',data.meetType2)
        formData.append('title',nextTitle)
        formData.append('country',data.country)
        formData.append('area',data.area)
        formData.append('address',nextAddress)
        formData.append('addressDetail',nextAddressDetail)
        formData.append('latitude',data.latitude)
        formData.append('longitude',data.longitude)
        formData.append('startTime',data.startTime)
        formData.append('endTime',data.endTime)
        formData.append('beginTime', beginTime)
        formData.append('finishTime', finishTime)
        formData.append('info1',data.info1)
        formData.append('info2',data.info2)
        formData.append('info3',data.info3)
        formData.append('info4',data.info4)
        formData.append('info5',data.info5)
        formData.append('info6',data.info6)
        formData.append('info7',data.info7)
        formData.append('paymentStatus',data.paymentStatus)
        formData.append('couponStatus',data.couponStatus)
        formData.append('visibleStatus',data.visibleStatus)
        formData.append('payQuantity', totalPayQuantity)
        formData.append('basicLike',data.basicLike)
        formData.append('rStatus',data.rStatus)
        formData.append('sStatus',data.sStatus)
        formData.append('isShow',data.isShow)
        formData.append('shippingPolicyType', data.shippingPolicyType)
        formData.append('shippingPrice', data.shippingPrice)

        if (isPackageProduct) {
            formData.append('isAnimalPackage', 'true')
            formData.append('packageCode', normalizeAnimalPackageCode(data.packageCode || ANIMAL_PACKAGE_CODE))
            formData.append('progressPackageCode', normalizeAnimalPackageCode(data.progressPackageCode))
            formData.append('requiredAreaM2', data.requiredAreaM2 || ANIMAL_PACKAGE_REQUIRED_AREA_M2)
        }

        getSubmittedCategoryRows().forEach((item, index) => formData.append(`categoryArr[${index}]`, JSON.stringify(item)))
        
        options.forEach((item, index) => {
            formData.append(`optionArr[${index}]`, JSON.stringify(item))
        })

        createData(formData, { isPackageProduct })
    }

    const handleClick = (e) => {
        e.target.select()
    }

    return (
        <Grid item xs={12}>
            <AddressSearchModal open={openModal} onClose={() => setOpenModal(false)} onComplete={handleComplete} />
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>{packageMode ? '패키지 상품 등록' : '애니멀런 등록'}</Title>
                <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }}>
                    <Prompt when={leaveGuardEnabled} message={ANIMAL_IMAGE_LEAVE_MESSAGE} />
                    <fieldset disabled={saving} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
                    {packageMode && (
                        <FormSection
                            title="패키지 식별 및 진행도"
                            description="판매 상품별 고유 코드를 지정하고, 활성화 및 메달 진행도를 공유할 기준 패키지를 선택합니다."
                        >
                            <AnimalPackageSharingFields data={data} setData={setData} />
                        </FormSection>
                    )}
                    <FormSection
                        title="코스 설정"
                        description={packageMode ? "서울25 25개 구 코스별 거리, 가격, 판매 수량을 입력합니다." : "코스별 거리, 가격, 판매 수량을 입력합니다."}
                    >
                        {arr.map((item, i) => {
                            return (
                                <Grid container spacing={2} sx={courseRowSx} key={i}>
                                    <Grid item xs={12} sm={6} md={2} >
                                        <FormControl fullWidth sx={{ marginRight:1 }} >
                                            <TextField
                                                label="코스"
                                                select
                                                id={'categoryType'+i}
                                                name={'categoryType'+i}
                                                value={item.type}
                                                onChange={(e) => onChangeOptionType(e, i)} 
                                                size="small"
                                            >
                                                <MenuItem value={0}>풀코스</MenuItem>
                                                <MenuItem value={1}>하프코스</MenuItem>
                                                <MenuItem value={2}>거리입력</MenuItem>
                                            </TextField>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={2} >
                                        <FormControl fullWidth sx={{ marginRight:1 }} >
                                            <TextField
                                                label="거리"
                                                required
                                                id={'categoryDistance'+i}
                                                name={'categoryDistance'+i}
                                                type="number"
                                                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                                                value={item.distance}
                                                onChange={(e) => {
                                                    let newArr = [...arr]
                                                    newArr[i].distance = e.target.value
                                                    setArr(newArr)
                                                }}
                                                onClick={handleClick}
                                                size="small"
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={2} >
                                        <FormControl fullWidth sx={{ marginRight:1 }} >
                                            <TextField
                                                label="가격"
                                                required
                                                id={'categoryPrice'+i}
                                                name={'categoryPrice'+i}
                                                type="number"
                                                InputProps={{ inputProps: { min: 0 } }}
                                                value={item.price}
                                                onChange={(e) => {
                                                    let newArr = [...arr]
                                                    newArr[i].price = e.target.value
                                                    setArr(newArr)
                                                }}
                                                onClick={handleClick}
                                                size="small"
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={2} >
                                        <FormControl fullWidth sx={{ marginRight:1 }} >
                                            <TextField
                                                label="코스명"
                                                id={'categoryName'+i}
                                                name={'categoryName'+i}
                                                value={item.name}
                                                onChange={(e) => {
                                                    let newArr = [...arr]
                                                    newArr[i].name = e.target.value
                                                    setArr(newArr)
                                                }}
                                                size="small"
                                            />
                                            
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={2} >
                                        <FormControl fullWidth sx={{ marginRight:1 }} >
                                            <TextField
                                                label="수량"
                                                required
                                                id={'categoryQuantity'+i}
                                                name={'categoryQuantity'+i}
                                                type="number"
                                                InputProps={{ inputProps: { min: 0, step: 1 } }}
                                                value={item.quantity}
                                                onChange={(e) => {
                                                    let newArr = [...arr]
                                                    newArr[i].quantity = e.target.value
                                                    setArr(newArr)
                                                }}
                                                onClick={handleClick}
                                                size="small"
                                            />
                                        </FormControl>
                                    </Grid>
                                    {i > 0 && 
                                        <Grid item xs={12} sm={6} md={2} >
                                            <Button variant="outlined" color="error" sx={{ ...FORM_INLINE_ACTION_BUTTON_SX, minWidth: 98 }} onClick={()=>removeInput(i)} startIcon={<RemoveIcon />}>코스삭제</Button>
                                        </Grid>
                                    }
                                </Grid>
                            )
                        })}
                        <Box sx={{ mt: 1}}>
                            <Button variant="contained" color="success" sx={{ ...FORM_INLINE_ACTION_BUTTON_SX, minWidth: 98 }} onClick={addInput} startIcon={<Add />}>코스추가</Button>
                        </Box>
                    </FormSection>
                    
                    <Grid container spacing={2} sx={{ mb: 1.5, alignItems: 'stretch' }}>
                    <Grid item xs={12} md={12} sx={{ display: 'flex' }}>
                    <FormSection
                        title="필수 옵션"
                        description="참가자가 반드시 선택해야 하는 옵션을 설정합니다."
                        sx={{ mb: 0, flex: 1 }}
                    >
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={12} md={12}>
                                <OptionStatusSetting
                                    label="필수옵션"
                                    value={data.rStatus}
                                    onChange={(value) => handleOptionStatusChange('rStatus', value)}
                                />
                            </Grid>
                        </Grid>
                        {data.rStatus === 1 && options.map((row, i) => {
                            return (
                                <Grid container spacing={2} sx={{ marginTop: 0 }} key={i}>
	                                    <Grid item xs={12} md={7} sm={7} >
                                        <FormControl fullWidth sx={{ marginRight:1 }} >
                                            <TextField
                                                label="옵션명"
                                                required
                                                id={'optionName'+i}
                                                name={'optionName'+i}
                                                value={row.name}
                                                onChange={(e) => {
                                                    let newOption = [...options]
                                                    newOption[i].name = e.target.value
                                                    setOptions(newOption)
                                                }}
                                                size="small"
                                            />
                                        </FormControl>
                                    </Grid>
	                                    <Grid item xs={12} md={5} sm={5} >
                                        <FormControl fullWidth sx={{ marginRight:1 }} >
                                            <TextField
                                                label="수량"
                                                required
                                                id={'optionQuantity'+i}
                                                name={'optionQuantity'+i}
                                                type="number"
                                                InputProps={{ inputProps: { min: 0 } }}
                                                value={row.quantity}
                                                onChange={(e) => {
                                                    let newOption = [...options]
                                                    newOption[i].quantity = e.target.value
                                                    setOptions(newOption)
                                                }}
                                                onClick={handleClick}
                                                size="small"
                                            />
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            )
                        })}
                    </FormSection>
                    </Grid>

                    <Grid item xs={12} md={12} sx={{ display: 'flex' }}>
                    <FormSection
                        title="선택 옵션"
                        description="추가 구매 가능한 옵션을 설정합니다."
                        sx={{ mb: 0, flex: 1 }}
                    >
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={12} md={12}>
                                <OptionStatusSetting
                                    label="선택옵션"
                                    value={data.sStatus}
                                    onChange={(value) => handleOptionStatusChange('sStatus', value)}
                                />
                            </Grid>
                        </Grid>
                        {Number(data.sStatus) === 1 && (
                            <Box sx={{ mt: 2 }}>
                                {selectionOptions.map((row, i) => (
                                    <Box key={row.id ?? i} sx={courseRowSx}>
                                        <Grid container spacing={1.5} alignItems="flex-start">
                                            <Grid item xs={12} md={3}>
                                                <FormControl fullWidth>
                                                    <TextField
                                                        label="옵션명"
                                                        required
                                                        value={row.name}
                                                        onChange={(e) => updateSelectionOption(i, 'name', e.target.value)}
                                                        size="small"
                                                    />
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={6} md={2}>
                                                <FormControl fullWidth>
                                                    <TextField
                                                        label="가격"
                                                        required
                                                        type="number"
                                                        InputProps={{ inputProps: { min: 0 } }}
                                                        value={row.price}
                                                        onChange={(e) => updateSelectionOption(i, 'price', e.target.value)}
                                                        onClick={handleClick}
                                                        size="small"
                                                    />
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={6} md={2}>
                                                <FormControl fullWidth>
                                                    <TextField
                                                        label="수량"
                                                        required
                                                        type="number"
                                                        InputProps={{ inputProps: { min: 0 } }}
                                                        value={row.quantity}
                                                        onChange={(e) => updateSelectionOption(i, 'quantity', e.target.value)}
                                                        onClick={handleClick}
                                                        size="small"
                                                    />
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={6} md={2}>
                                                <FormControl fullWidth>
                                                    <TextField
                                                        select
                                                        label="웹 노출"
                                                        value={Number(row.webVisible ?? 1)}
                                                        onChange={(e) => updateSelectionOption(i, 'webVisible', Number(e.target.value))}
                                                        size="small"
                                                    >
                                                        {VISIBILITY_OPTIONS.map((option) => (
                                                            <MenuItem key={option.value} value={option.value}>
                                                                {option.label}
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={6} md={2}>
                                                <FormControl fullWidth>
                                                    <TextField
                                                        select
                                                        label="앱 노출"
                                                        value={Number(row.appVisible ?? 0)}
                                                        onChange={(e) => updateSelectionOption(i, 'appVisible', Number(e.target.value))}
                                                        size="small"
                                                    >
                                                        {VISIBILITY_OPTIONS.map((option) => (
                                                            <MenuItem key={option.value} value={option.value}>
                                                                {option.label}
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} md={1}>
                                                <Button
                                                    fullWidth
                                                    variant="outlined"
                                                    color="error"
                                                    startIcon={<RemoveIcon />}
                                                    disabled={selectionOptions.length <= 1}
                                                    onClick={() => removeSelectionOption(i)}
                                                    sx={FORM_INLINE_ACTION_BUTTON_SX}
                                                >
                                                    삭제
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                ))}
                                <Box sx={{ mt: 1.5 }}>
                                    <Button variant="outlined" startIcon={<Add />} onClick={addSelectionOption} sx={FORM_INLINE_ACTION_BUTTON_SX}>
                                        선택옵션 추가
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </FormSection>
                    </Grid>
                    </Grid>

                    <FormSection
                        title="이미지"
                        description={packageMode ? "메인이미지와 상세이미지를 등록합니다. 완주기록증이미지는 패키지 상품에서 비활성화됩니다." : "메인이미지, 상세이미지, 완주기록증이미지를 한 줄에서 확인하고 교체합니다."}
                        sx={{ mt: 2 }}
                    >
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4} md={4}>
                            <ImageUploadCard
                                title="메인이미지"
                                image={mainImage}
                                imageAlt="메인이미지"
                                inputId="mainImage"
                                inputName="mainImage"
                                uploadType={1}
                                onUpload={handleUploadClick}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4} md={4}>
                            <ImageUploadCard
                                title="상세이미지"
                                image={subImage1}
                                imageAlt="상세이미지"
                                inputId="subImage1"
                                inputName="subImage1"
                                uploadType={2}
                                onUpload={handleUploadClick}
                                canClear
                                hasPendingFile={data.subImage1 instanceof File}
                                onClear={() => handleClearSelectedImage('subImage1', 'subImage1')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4} md={4}>
                            <ImageUploadCard
                                title="완주기록증이미지"
                                image={recordImage}
                                imageAlt="완주기록증이미지"
                                inputId="recordImage"
                                inputName="recordImage"
                                uploadType={3}
                                onUpload={handleUploadClick}
                                canClear
                                hasPendingFile={data.recordImage instanceof File}
                                onClear={() => handleClearSelectedImage('recordImage', 'recordImage')}
                                disabled={packageMode}
                            />
                        </Grid>
                    </Grid>
                    </FormSection>

                    <FormSection
                        title="일정"
                        description="대회 타입과 접수/진행 기간을 설정합니다."
                    >
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={12} sm={12} >
                            <FormControl fullWidth>
                                <TextField
                                    label="대회타입"
                                    select
                                    id="meetType2"
                                    name="meetType2"
                                    value={data.meetType2}
                                    onChange={handleUpdateField} 
                                    size="small"
                                >
                                    <MenuItem value={1}>애니멀런</MenuItem>
                                    <MenuItem value={2}>댕댕이레이스</MenuItem>
                                    <MenuItem value={3}>산넘어산</MenuItem>
                                    <MenuItem value={4}>B급마라톤</MenuItem>
                                </TextField>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} md={6} sm={6}>
                            <FormControl fullWidth>
                                <LocalizationProvider dateAdapter={AdapterDateFns} locale={koLocale}>
                                    <DesktopDatePicker
                                        label="접수시작일"
                                        inputFormat="yyyy-MM-dd"
                                        mask={"____-__-__"}
                                        value={data.startTime}
                                        onChange={d => setData({
                                            ...data,
                                            startTime: d
                                        })}
                                        renderInput={(params) => <TextField required {...params} size="small"/>}
                                    />
                                </LocalizationProvider>
                                
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} md={6} sm={6}>
                            <FormControl fullWidth>
                                <LocalizationProvider dateAdapter={AdapterDateFns} locale={koLocale}>
                                    <DesktopDatePicker
                                        label="접수종료일"
                                        inputFormat="yyyy-MM-dd"
                                        mask={"____-__-__"}
                                        value={data.endTime}
                                        minDate={data.startTime}
                                        onChange={d => setData({
                                            ...data,
                                            endTime: d
                                        })}
                                        renderInput={(params) => <TextField required {...params} size="small" />}
                                    />
                                </LocalizationProvider>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} md={6} sm={6}>
                            <FormControl fullWidth>
                                <LocalizationProvider dateAdapter={AdapterDateFns} locale={koLocale}>
                                    <DesktopDatePicker
                                        label="대회시작일"
                                        inputFormat="yyyy-MM-dd"
                                        mask={"____-__-__"}
                                        value={beginDay}
                                        // minDate={data.endTime}
                                        onChange={d => setBeginDay(d)}
                                        renderInput={(params) => <TextField required {...params} size="small" />}
                                    />
                                </LocalizationProvider>
                            </FormControl>
                        </Grid>
                        {/* <Grid item xs={3} md={3} sm={6}>
                            <FormControl sx={{ width: '10ch' }}>
                                <TextField
                                    label="시"
                                    select
                                    id="beginHours"
                                    name="beginHours"
                                    value={beginHours}
                                    onChange={(e) => setBeginHours(e.target.value)}
                                    size="small"
                                >
                                    <MenuItem value={0}>0</MenuItem>
                                    <MenuItem value={1}>1</MenuItem>
                                    <MenuItem value={2}>2</MenuItem>
                                    <MenuItem value={3}>3</MenuItem>
                                    <MenuItem value={4}>4</MenuItem>
                                    <MenuItem value={5}>5</MenuItem>
                                    <MenuItem value={6}>6</MenuItem>
                                    <MenuItem value={7}>7</MenuItem>
                                    <MenuItem value={8}>8</MenuItem>
                                    <MenuItem value={9}>9</MenuItem>
                                    <MenuItem value={10}>10</MenuItem>
                                    <MenuItem value={11}>11</MenuItem>
                                    <MenuItem value={12}>12</MenuItem>
                                    <MenuItem value={13}>13</MenuItem>
                                    <MenuItem value={14}>14</MenuItem>
                                    <MenuItem value={15}>15</MenuItem>
                                    <MenuItem value={16}>16</MenuItem>
                                    <MenuItem value={17}>17</MenuItem>
                                    <MenuItem value={18}>18</MenuItem>
                                    <MenuItem value={19}>19</MenuItem>
                                    <MenuItem value={20}>20</MenuItem>
                                    <MenuItem value={21}>21</MenuItem>
                                    <MenuItem value={22}>22</MenuItem>
                                    <MenuItem value={23}>23</MenuItem>
                                </TextField>
                            </FormControl>
                            <FormControl sx={{ width: '10ch' }}>
                                <TextField
                                    label="분"
                                    select
                                    id="beginMinutes"
                                    name="beginMinutes"
                                    value={beginMinutes}
                                    onChange={(e) => setBeginMinutes(e.target.value)}
                                    size="small"
                                >
                                    <MenuItem value={0}>0</MenuItem>
                                    <MenuItem value={10}>10</MenuItem>
                                    <MenuItem value={20}>20</MenuItem>
                                    <MenuItem value={30}>30</MenuItem>
                                    <MenuItem value={40}>40</MenuItem>
                                    <MenuItem value={50}>50</MenuItem>
                                </TextField>
                            </FormControl>
                        </Grid> */}
                        <Grid item xs={6} md={6} sm={6}>
                            <FormControl fullWidth>
                                <LocalizationProvider dateAdapter={AdapterDateFns} locale={koLocale}>
                                    <DesktopDatePicker
                                        label="대회종료일"
                                        inputFormat="yyyy-MM-dd"
                                        mask={"____-__-__"}
                                        value={finishDay}
                                        minDate={beginDay}
                                        onChange={d => setFinishDay(d)}
                                        renderInput={(params) => <TextField required {...params} size="small" />}
                                    />
                                </LocalizationProvider>
                            </FormControl>
                        </Grid>
                        {/* <Grid item xs={3} md={3} sm={6}>
                            <FormControl sx={{ width: '10ch' }}>
                                <TextField
                                    label="시"
                                    select
                                    id="finishHours"
                                    name="finishHours"
                                    value={finishHours}
                                    onChange={(e) => setFinishHours(e.target.value)}
                                    size="small"
                                >
                                    <MenuItem value={0}>0</MenuItem>
                                    <MenuItem value={1}>1</MenuItem>
                                    <MenuItem value={2}>2</MenuItem>
                                    <MenuItem value={3}>3</MenuItem>
                                    <MenuItem value={4}>4</MenuItem>
                                    <MenuItem value={5}>5</MenuItem>
                                    <MenuItem value={6}>6</MenuItem>
                                    <MenuItem value={7}>7</MenuItem>
                                    <MenuItem value={8}>8</MenuItem>
                                    <MenuItem value={9}>9</MenuItem>
                                    <MenuItem value={10}>10</MenuItem>
                                    <MenuItem value={11}>11</MenuItem>
                                    <MenuItem value={12}>12</MenuItem>
                                    <MenuItem value={13}>13</MenuItem>
                                    <MenuItem value={14}>14</MenuItem>
                                    <MenuItem value={15}>15</MenuItem>
                                    <MenuItem value={16}>16</MenuItem>
                                    <MenuItem value={17}>17</MenuItem>
                                    <MenuItem value={18}>18</MenuItem>
                                    <MenuItem value={19}>19</MenuItem>
                                    <MenuItem value={20}>20</MenuItem>
                                    <MenuItem value={21}>21</MenuItem>
                                    <MenuItem value={22}>22</MenuItem>
                                    <MenuItem value={23}>23</MenuItem>
                                </TextField>
                            </FormControl>
                            <FormControl sx={{ width: '10ch' }}>
                                <TextField
                                    label="분"
                                    select
                                    id="finishMinutes"
                                    name="finishMinutes"
                                    value={finishMinutes}
                                    onChange={(e) => setFinishMinutes(e.target.value)}
                                    size="small"
                                >
                                    <MenuItem value={0}>0</MenuItem>
                                    <MenuItem value={10}>10</MenuItem>
                                    <MenuItem value={20}>20</MenuItem>
                                    <MenuItem value={30}>30</MenuItem>
                                    <MenuItem value={40}>40</MenuItem>
                                    <MenuItem value={50}>50</MenuItem>
                                </TextField>
                            </FormControl>
                        </Grid> */}
                    </Grid>
                    </FormSection>

                    <FormSection
                        title="대회 정보"
                        description="운영자가 앱에 노출할 기본 문구와 문의 정보를 입력합니다."
                    >
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={12} md={12}>
                            <FormControl fullWidth>
                                <TextField
                                    label="대회명"
                                    required
                                    id="title"
                                    name="title"
                                    value={data.title}
                                    onChange={handleUpdateField}
                                    size="small"
                                />
                            </FormControl>
                        </Grid>
                        { data.meetType === 2 && 
                            <>
                            <Grid item xs={6} md={6} sm={6}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="국가명"
                                        required
                                        id="country"
                                        name="country"
                                        value={data.country}
                                        onChange={handleUpdateField}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} md={6} sm={6}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="도시"
                                        required
                                        id="area"
                                        name="area"
                                        value={data.area}
                                        onChange={handleUpdateField}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                            </>
                        }
                        <Grid item xs={6} md={6} sm={6}>
                            <FormControl fullWidth>
                                    <TextField
                                        label="주소"
                                        id="address"
                                        name="address"
                                        value={data.address}
                                        onChange={handleUpdateField}
                                        size="small"
                                    />
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} md={6} sm={6}>
                            <FormControl fullWidth>
                                <TextField
                                    label="상세주소"
                                    id="addressDetail"
                                    name="addressDetail"
                                    value={data.addressDetail}
                                    onChange={handleUpdateField}
                                    size="small"
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth>
                                <TextField
                                    label="문의"
                                    id="info2"
                                    name="info2"
                                    value={data.info2}
                                    onChange={handleUpdateField}
                                    size="small"
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth>
                                <TextField
                                    label="주최"
                                    id="info4"
                                    name="info4"
                                    value={data.info4}
                                    onChange={handleUpdateField}
                                    size="small"
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth>
                                <TextField
                                    label="홈페이지"
                                    id="info1"
                                    name="info1"
                                    value={data.info1}
                                    onChange={handleUpdateField}
                                    size="small"
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth>
                                <TextField
                                    label="협찬"
                                    id="info3"
                                    name="info3"
                                    value={data.info3}
                                    onChange={handleUpdateField}
                                    size="small"
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                    </FormSection>

                    <FormSection
                        title="배송 정책"
                        description="상품별 배송비와 무료배송 조건을 설정합니다. 코스별이 아닌 productId별로 적용됩니다."
                    >
                        <AnimalShippingPolicyFields data={data} setData={setData} />
                    </FormSection>

                    <FormSection
                        title="판매/노출"
                        description="결제, 쿠폰, 판매 수량, 노출 여부를 설정합니다."
                        sx={{ mb: 0 }}
                    >
                    <Grid container spacing={2} columns={{ xs: 1, sm: 2, md: 10 }}>
                        <Grid item xs={1} sm={1} md={2}>
                            <FormControl fullWidth>
                                <TextField
                                    label="결제가능"
                                    select
                                    id="paymentStatus"
                                    name="paymentStatus"
                                    value={data.paymentStatus}
                                    onChange={handleUpdateField}
                                    size="small"
                                >
                                    <MenuItem value={1}>가능</MenuItem>
                                    <MenuItem value={0}>불가능</MenuItem>
                                </TextField>
                            </FormControl>
                        </Grid>
                        <Grid item xs={1} sm={1} md={2}>
                            <FormControl fullWidth>
                                <TextField
                                    label="쿠폰가능"
                                    select
                                    id="couponStatus"
                                    name="couponStatus"
                                    value={data.couponStatus}
                                    onChange={handleUpdateField}
                                    size="small"
                                >
                                    <MenuItem value={1}>가능</MenuItem>
                                    <MenuItem value={0}>불가능</MenuItem>
                                </TextField>
                            </FormControl>
                        </Grid>
                        <Grid item xs={1} sm={1} md={2}>
                            <FormControl fullWidth>
                                <TextField
                                    label="전체 판매제한수량"
                                    type="number"
                                    id="payQuantitySummary"
                                    value={totalLimitQuantity}
                                    disabled
                                    size="small"
                                    InputProps={{ inputProps: { min: 0, step: 1 } }}
                                    helperText="코스별 수량 합계로 자동 계산됩니다."
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={1} sm={1} md={2}>
                            <FormControl fullWidth>
                                <TextField
                                    label="기본좋아요수"
                                    type="number"
                                    id="basicLike"
                                    name="basicLike"
                                    value={data.basicLike}
                                    onChange={handleUpdateField}
                                    onClick={handleClick}
                                    size="small"
                                    InputProps={{ inputProps: { min: 0, step: 1 } }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={1} sm={1} md={2}>
                            <FormControl fullWidth>
                                <TextField
                                    label="노출여부"
                                    select
                                    id="isShow"
                                    name="isShow"
                                    value={data.isShow}
                                    onChange={handleUpdateField}
                                    size="small"
                                >
                                    <MenuItem value={1}>노출</MenuItem>
                                    <MenuItem value={0}>미노출</MenuItem>
                                </TextField>
                            </FormControl>
                        </Grid>
                    </Grid>
                    </FormSection>
                    </fieldset>
                    <BottomButtonGroup
                        variant="formAction"
                        visible={true}
                        submitDisabled={saving}
                        submitLabel={saving ? '저장 중...' : '저장'}
                        backDisabled={saving}
                        onBackClick={handleBackClick}
                    />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
